import React, { createContext, Dispatch, ReactNode, SetStateAction, useContext, useMemo } from 'react';
import useDashboardsEffects from '../../hooks/context/effects/useDashboardsEffects';
import useDashboardsContext from '../../hooks/context/useDashboardsContext';
import { Dashboard, DashboardQueriesType, DashboardQuery } from '../../types/files';
import storage from '../../utils/storage';
import { AuthContext } from '../AuthContext';

export type OverviewQueries = {
  [key: string]: number | string | string[];
};

export type OverviewQueriesList = {
  [key: string]: OverviewQueries;
};

export type StatusGroupList = {
  [key: string]: Dashboard[];
};

type DashboardsContextProps = {
  dashboardsLoaded: boolean;
  setDashboardsLoaded: Dispatch<SetStateAction<boolean>>;
  dashboards: Dashboard[];
  setDashboards: Dispatch<SetStateAction<Dashboard[]>>;
  dashboardsCollection: Record<string, DashboardQueriesType[]>;
  setDashboardsCollection: Dispatch<SetStateAction<Record<string, DashboardQueriesType[]>>>;
  externalDashboards: Dashboard[];
  setExternalDashboards: Dispatch<SetStateAction<Dashboard[]>>;
  dashboardsQueries: DashboardQuery[],
  setDashboardsQueries: Dispatch<SetStateAction<DashboardQuery[]>>,
  mappedOverviewQueries: Map<string, any>;
  isPublicView: boolean;
  setPublicView: Dispatch<SetStateAction<boolean>>;
  isShared: boolean;
  setShared: Dispatch<SetStateAction<boolean>>;
};

export const DashboardsContext = createContext<DashboardsContextProps>({
  dashboardsLoaded: false,
  setDashboardsLoaded: () => {},
  dashboards: [],
  setDashboards: () => {},
  dashboardsCollection: {},
  setDashboardsCollection: () => {},
  externalDashboards: [],
  setExternalDashboards: () => {},
  dashboardsQueries: [],
  setDashboardsQueries: () => {},
  mappedOverviewQueries: new Map(),
  isPublicView: false,
  setPublicView: () => {},
  isShared: false,
  setShared: () => {},
});

export const OverviewQueryTitles = ['Digest', 'Team', 'Key People', 'Note Metrics', 'Investment Thesis', 'Location', 'Website', 'Crunchbase Page', 'LinkedIn'];
export const DisplayQueryTitles = ['Description', 'Website', 'LinkedIn', 'Location', 'Team', 'Metrics'];
export const CompanyInfoFields = ['Description', 'Stage', 'Sector', 'Team', 'Deck', 'Deal overview'];
export const ExportableFields = ['Key People', 'Description', 'Market', 'Product', 'Business Model', 'Go To Market', 'Custom Fields', 'Location', 'Founded', 'Website', 'LinkedIn',];

const DashboardsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { userGroup } = useContext(AuthContext);
  const {
    dashboardsLoaded,
    dashboards,
    externalDashboards,
    dashboardsCollection,
    dashboardsQueries,
    mappedOverviewQueries,
    isPublicView,
    isShared,
    setDashboardsLoaded,
    setDashboards,
    setExternalDashboards,
    setDashboardsCollection,
    setDashboardsQueries,
    setIsPublicView,
    setIsShared,
  } = useDashboardsContext();
  useDashboardsEffects(userGroup);

  const contextValue = useMemo(() => ({
    dashboardsLoaded,
    setDashboardsLoaded,
    dashboards,
    setDashboards,
    externalDashboards,
    setExternalDashboards,
    dashboardsCollection,
    setDashboardsCollection,
    dashboardsQueries,
    setDashboardsQueries,
    mappedOverviewQueries,
    isPublicView,
    setPublicView: setIsPublicView,
    isShared,
    setShared: setIsShared,
  }), [
    dashboardsLoaded,
    setDashboardsLoaded,
    dashboards,
    setDashboards,
    externalDashboards,
    setExternalDashboards,
    dashboardsCollection,
    setDashboardsCollection,
    dashboardsQueries,
    setDashboardsQueries,
    mappedOverviewQueries,
    isPublicView,
    setIsPublicView,
    isShared,
    setIsShared,
  ]);

  if (storage.getItem('config.logger-context'))
    console.log("CTX", ['Dashboards', contextValue]);

  return (
    <DashboardsContext.Provider value={contextValue}>
      {children}
    </DashboardsContext.Provider>
  );
};

export default DashboardsProvider;
